'use client';

/**
 * components/SessionAlert.tsx — qui est connecté, et qui peut agir.
 *
 * LA VERSION D'ORIGINE ARMAIT L'INTRUS : « ce n'est pas moi » était offert à
 * toutes les sessions, donc aussi à celui qui venait de voler le mot de passe,
 * qui pouvait expulser le propriétaire d'un geste (signalé le 10/08).
 *
 * LE GESTE NE TUE PLUS RIEN — IL GÈLE. Contester met le compte en attente :
 * plus aucune écriture n'aboutit, pour personne. Seule une CLÉ D'ACCÈS
 * vérifiée (empreinte, visage) lève le gel, et c'est cette vérification qui
 * ferme les autres sessions, côté serveur. Un intrus peut geler — il se gèle
 * avec — mais il ne peut pas franchir la clé : elle est dans l'enclave du
 * téléphone du propriétaire, et l'empreinte n'en sort jamais.
 *
 * Trois raisons d'apparaître, dans cet ordre de gravité :
 *   1. le compte est GELÉ         → prouver, tout de suite ;
 *   2. cet appareil est INCONNU   → le valider par la clé ;
 *   3. une autre session est ouverte → c'est moi / ce n'est pas moi.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCompte, seDeconnecter } from '@/lib/auth/client';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

/** Ce que la carte annonce une fois le geste passé. `asked` n'est PAS une
 *  réussite : c'est une demande envoyée qui attend une réponse ailleurs — elle
 *  se distingue des trois autres partout où l'écran se félicite. */
type Done = null | 'cleared' | 'trusted' | 'created' | 'asked';

/**
 * Nommer l'appareil — aussi précisément que le web le permet, et pas plus.
 *
 * CE QUE LE NAVIGATEUR DONNE VRAIMENT, parce que la limite compte ici :
 *   — Android annonce son modèle exact dans l'agent utilisateur
 *     (« TECNO Spark 6 »). On le lit, c'est le meilleur nom possible.
 *   — iOS ne l'annonce JAMAIS : Safari dit « iPhone » ou « iPad », un point
 *     c'est tout. Apple le masque volontairement, contre le pistage. Aucune
 *     bibliothèque ne fait mieux ; celles qui prétendent le contraire devinent
 *     à partir de la taille d'écran, et se trompent à chaque nouveau modèle.
 *
 * D'où le choix : on donne le nom le plus juste qu'on puisse PROUVER, et on
 * laisse renommer. « iPhone » qu'on rebaptise « iPhone de Bibi » vaut mieux
 * qu'un « iPhone 13 Pro Max » deviné qui se révélerait faux.
 */
export const myLabel = () => {
  const ua = navigator.userAgent;

  // Android : le modèle est dans l'agent, entre la version et le constructeur.
  const android = ua.match(/Android[^;]*;\s*([^;)]+?)(?:\s+Build)?[;)]/);
  if (android?.[1]) {
    const modele = android[1].trim();
    if (modele && !/^wv$/i.test(modele)) return modele.slice(0, 40);
  }

  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  // iPadOS se fait passer pour un Mac de bureau depuis iOS 13 : seul le tactile
  // le trahit. Sans ça, tous les iPad s'appellent « Safari · Ordinateur ».
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';

  const nav = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome'
    : /Safari/.test(ua) ? 'Safari' : /Firefox/.test(ua) ? 'Firefox' : 'Navigateur';
  const kind = /Mobi/.test(ua) ? 'Mobile' : /Tablet/.test(ua) ? 'Tablette' : 'Ordinateur';
  return `${nav} · ${kind}`;
};

/** La famille d'appareil, qui pilote l'icône de la liste. */
export const myKind = (): 'phone' | 'tablet' | 'desktop' => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/.test(ua)) return 'tablet';
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'tablet';
  if (/Android/.test(ua) && !/Mobi/.test(ua)) return 'tablet';
  if (/Mobi|iPhone|Android/.test(ua)) return 'phone';
  return 'desktop';
};

const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round', strokeLinejoin: 'round',
} as const;

/** Le geste complet côté navigateur : demander les options, laisser l'appareil
 *  demander l'empreinte, renvoyer la signature. Partagé avec l'écran Sécurité. */
export async function verifyWithPasskey(): Promise<void> {
  const o = await fetch('/api/security', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'verify-options' }),
  });
  const opts = await o.json();
  if (!o.ok) throw new Error(opts?.error ?? 'Vérification indisponible.');

  const response = await startAuthentication({ optionsJSON: opts });

  const v = await fetch('/api/security', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'verify', response }),
  });
  const j = await v.json();
  if (!v.ok) throw new Error(j?.error ?? 'Vérification refusée.');
}

/**
 * Créer une clé d'accès sur CET appareil.
 *
 * LE CAS DU TROUSSEAU SYNCHRONISÉ, signalé le 11/08 : l'iPad affichait « aucune
 * clé » et répondait « the authenticator was previously registered » dès qu'on
 * essayait d'en créer une. Les deux étaient vrais en même temps — la clé était
 * née sur un autre appareil et le trousseau iCloud l'avait recopiée, donc le
 * navigateur la connaissait alors que la base ne la rattachait pas à cet
 * appareil.
 *
 * `InvalidStateError` n'est pas une erreur ici : c'est le navigateur qui dit
 * « j'ai déjà ta clé ». On la fait alors VÉRIFIER, ce qui prouve qu'elle est
 * bien là, et l'appareil est marqué comme protégé. Le résultat est celui que la
 * personne attendait, sans message incompréhensible.
 */
export async function createPasskey(): Promise<void> {
  const o = await fetch('/api/security', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'passkey-options' }),
  });
  const opts = await o.json();
  if (!o.ok) throw new Error(opts?.error ?? 'Création indisponible.');

  let response;
  try {
    response = await startRegistration({ optionsJSON: opts });
  } catch (e) {
    if (e instanceof Error && e.name === 'InvalidStateError') {
      await verifyWithPasskey();
      return;
    }
    throw e;
  }

  const v = await fetch('/api/security', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'passkey-register', response, label: myLabel() }),
  });
  const j = await v.json();
  if (!v.ok) throw new Error(j?.error ?? 'Clé refusée.');
}

type Sec = { hasPasskey: boolean; held: boolean };
type DeviceRow = {
  id: string; label: string | null; status: string;
  isPrimary: boolean; isCurrent: boolean; hasPasskey: boolean;
  requestedAt: string | null; lastSeenAt: string | null;
};
type Dev = {
  trusted: boolean; hasTrustedElsewhere: boolean;
  strict: boolean; waiting: DeviceRow[]; primaryLabel: string | null;
  approvalRequested: boolean; rejected: boolean;
  /** L'heure à laquelle la demande de CET appareil tombe. */
  approvalExpiresAt?: string | null;
  /** En attente, mais personne ne poireaute en direct — cf. `dormant` côté
   *  serveur. On les annonce sans bloquer l'écran. */
  dormant?: DeviceRow[];
  /** Connectés au compte sans rien demander. Purement informatif. */
  arrivees?: DeviceRow[];
};

/**
 * Prouve la présence de la personne, puis exécute l'action sur /api/devices.
 *
 * C'EST LE CŒUR DE LA CORRECTION DU 11/08. « C'est moi » n'était qu'un drapeau
 * écrit en local : n'importe qui pouvait le cliquer, et l'appareil entrait. Un
 * bouton qui ne demande rien ne protège de rien. Toute décision sur les
 * appareils passe désormais par un geste biométrique, vérifié côté serveur.
 */
export async function proveOn(action: string, deviceId?: string): Promise<void> {
  const o = await fetch('/api/security', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'verify-options' }),
  });
  const opts = await o.json();
  if (!o.ok) throw new Error(opts?.error ?? 'Vérification indisponible.');

  const response = await startAuthentication({ optionsJSON: opts });

  const r = await fetch('/api/devices', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, deviceId, response }),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j?.error ?? 'Action refusée.');
}

/** Lever la main depuis un appareil en attente. Ne prouve rien, et n'a rien à
 *  prouver : ça n'accorde aucun droit, ça affiche une demande ailleurs. */
export async function askApproval(): Promise<void> {
  const r = await fetch('/api/devices', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'request-approval' }),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j?.error ?? 'Demande impossible.');
}

export function SessionAlert() {
  const { isLoaded, user } = useCompte();

  const [sec, setSec] = useState<Sec | null>(null);
  const [dev, setDev] = useState<Dev | null>(null);
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Done>(null);
  const [err, setErr] = useState<string | null>(null);

  /** Cet appareil a-t-il demandé l'autorisation ? La demande explicite est le
   *  seul déclencheur du retour « Appareil validé ». */
  const wasWaiting = useRef(false);
  /** Les appareils dormants écartés d'un « Plus tard ». Vidé au rechargement,
   *  et pas avant : la carte ne doit pas revenir quatre secondes plus tard, au
   *  prochain sondage, mais elle revient au prochain lancement. */
  const [écartés, setÉcartés] = useState<string[]>([]);
  /** Les arrivées déjà annoncées pendant cette session d'onglet. Une connexion
   *  s'annonce UNE fois : la répéter à chaque sondage de quatre secondes
   *  transformerait l'information en harcèlement. `sessionStorage` suffit — au
   *  prochain lancement, la pastille de Sécurité prend le relais. */
  const [annoncées, setAnnoncées] = useState<string[]>([]);
  useEffect(() => {
    /* Lecture unique d'un système extérieur à React, au montage. Pas de cascade :
       les dépendances sont vides, l'effet ne se rejoue jamais. Lire dans le
       rendu ferait diverger le HTML du serveur de celui du navigateur. */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    try { setAnnoncées(JSON.parse(sessionStorage.getItem('ogryn_arrivees') ?? '[]')); } catch { /* onglet privé */ }
  }, []);

  /** L'horloge du décompte. Elle ne bat QUE pendant l'attente : un `setInterval`
   *  d'une seconde qui tourne en permanence réveille l'appareil pour rien. */
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const scan = useCallback(async () => {
    if (!user) return;
    try {
      const [s, d] = await Promise.all([
        fetch('/api/security').then(r => (r.ok ? r.json() : null)),
        fetch('/api/devices', { headers: { 'x-device-label': myLabel(), 'x-device-kind': myKind() } }).then(r => (r.ok ? r.json() : null)),
      ]);
      if (s) setSec(s);
      if (d) setDev(d);
      // Le refus est un signal consommable. Le serveur ferme aussi la session
      // ciblée, mais ce garde rend l'interface cohérente immédiatement.
      if (d?.rejected) {
        await seDeconnecter('/bienvenue');
        return;
      }
      // Le passage ne peut commencer qu'après le clic « Demander » : un appareil
      // simplement pending ne doit jamais afficher « Appareil validé ».
      if (d && !d.trusted && d.approvalRequested) wasWaiting.current = true;
      if (d?.trusted && wasWaiting.current) {
        wasWaiting.current = false;
        setDone('trusted');
      }
    } catch { return; }
  }, [user]);

  useEffect(() => {
    if (!isLoaded) return;
    /* `scan` n'écrit dans l'état qu'APRÈS l'aller-retour réseau, jamais dans le
       rendu qui la déclenche : la règle ne suit pas l'`await` et lit l'appel
       comme synchrone. Il n'y a pas de rendu en cascade ici. */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void scan();
    const onVisible = () => { if (document.visibilityState === 'visible') void scan(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isLoaded, scan]);

  const held = sec?.held === true;
  const unknownDevice = !held && dev !== null && !dev.trusted && dev.hasTrustedElsewhere;
  /** Des appareils demandent à entrer, et CET appareil a le droit de trancher.
   *  C'est la contrepartie du régime strict : quelqu'un doit voir la demande,
   *  sinon le nouvel appareil attend indéfiniment sans savoir pourquoi. */
  const enDirect = !held && dev?.trusted === true ? (dev.waiting ?? []) : [];
  /** Les mêmes, mais sans personne derrière : le demandeur a fermé
   *  l'application, ou sa demande a expiré. Ils s'annoncent quand même — c'est
   *  la seule façon d'apprendre, en se connectant, que deux appareils
   *  attendaient depuis hier (19/08). */
  const dormants = (!held && dev?.trusted === true ? (dev.dormant ?? []) : [])
    .filter(d => !écartés.includes(d.id));
  const toApprove = enDirect.length > 0 ? enDirect : dormants;
  const hasRequests = toApprove.length > 0;
  /** Personne n'attend derrière : on informe, on ne barre pas la route. */
  const dort = enDirect.length === 0 && dormants.length > 0;

  /**
   * UN APPAREIL S'EST CONNECTÉ — ET PERSONNE NE LE SAVAIT (20/09).
   *
   * Tant qu'il n'avait pas cliqué « Demander l'autorisation », un appareil
   * entré sur le compte n'apparaissait NULLE PART sur les autres. On voyait la
   * demande d'accès, jamais la connexion. C'est pourtant l'événement qu'un
   * propriétaire veut apprendre tout de suite, et le seul qui n'exige rien de
   * lui : il n'y a rien à trancher, juste à savoir.
   *
   * Donc : jamais de voile, aucun bouton de décision, et ça part tout seul.
   */
  const arrivées = (!held && dev?.trusted === true ? (dev.arrivees ?? []) : [])
    .filter(d => !annoncées.includes(d.id));
  /** Clé stable : sans elle, le tableau fraîchement construit à chaque rendu
   *  relancerait le minuteur de quatre secondes indéfiniment. */
  const clésArrivées = arrivées.map(d => d.id).join(',');

  /** On n'annonce une arrivée que quand l'écran n'a rien de plus grave à dire :
   *  une question de sécurité en cours passe avant une information. */
  const annonce = !held && !unknownDevice && !hasRequests && done === null && arrivées.length > 0;


  /** Combien de temps la demande de cet appareil tient encore. `null` = pas de
   *  demande en cours. À zéro, le bouton redevient « Demander l'autorisation » :
   *  attendre sans savoir jusqu'à quand, c'est ce qui fait rappuyer toutes les
   *  vingt secondes en croyant que rien n'est parti (19/08). */
  const échéance = dev?.approvalExpiresAt ? Date.parse(dev.approvalExpiresAt) : null;
  const restant = échéance === null ? null : Math.max(0, Math.round((échéance - maintenant) / 1000));
  const chrono = restant === null ? null
    : `${Math.floor(restant / 60)}:${String(restant % 60).padStart(2, '0')}`;
  const expiré = restant === 0;
  /** L'attente telle que le bouton doit la dire. */
  const attend = !expiré && (dev?.approvalRequested === true || done === 'asked');

  useEffect(() => {
    if (échéance === null) return;
    const t = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(t);
  }, [échéance]);

  /** Tous les appareils de confiance sondent, pas seulement un « principal » :
   *  la demande doit apparaître sur l'iPad, l'iPhone et le Mac connectés. Le
   *  nouvel appareil sonde aussi pour apprendre la décision. */
  useEffect(() => {
    if (!unknownDevice && dev?.trusted !== true) return;
    const t = setInterval(() => void scan(), 4000);
    return () => clearInterval(t);
  }, [unknownDevice, dev?.trusted, scan]);

  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setDone(null); setErr(null); setClosing(false);
      setDev(d => (d ? { ...d, hasTrustedElsewhere: false } : d));
    }, 260);
  }, []);

  useEffect(() => {
    if (done !== 'trusted') return;
    const timer = window.setTimeout(dismiss, 3000);
    return () => window.clearTimeout(timer);
  }, [done, dismiss]);

  /* L'annonce d'arrivée s'efface seule au bout de quatre secondes — le temps de
     la lire, pas celui de devoir la fermer. Marquer les identifiants AVANT de
     retirer la carte évite qu'elle revienne au sondage suivant. */
  useEffect(() => {
    if (!annonce) return;
    const ids = clésArrivées.split(',').filter(Boolean);
    const timer = window.setTimeout(() => {
      setAnnoncées(v => {
        const suite = [...new Set([...v, ...ids])];
        try { sessionStorage.setItem('ogryn_arrivees', JSON.stringify(suite)); } catch { /* onglet privé */ }
        return suite;
      });
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [annonce, clésArrivées]);

  if (!held && !unknownDevice && !hasRequests && !done && !annonce) return null;

  /** Une seule question partout : la passkey prouve la personne, puis le
   *  choix approuve ou refuse LA DEMANDE du nouvel appareil. */
  const decide = (id: string, oui: boolean) =>
    run(async () => {
      await proveOn(oui ? 'approve' : 'reject', id);
      if (!oui) dismiss();
    }, oui ? 'trusted' : null);

  /** Depuis un appareil en attente : lever la main, et ATTENDRE.
   *
   *  Ce geste annonçait « Appareil validé » — c'était `'trusted'` ici. Il ne
   *  valide rien : il pose une demande sur les autres appareils, et le serveur
   *  ne change RIEN tant que personne n'a répondu. Cet écran mentait donc au
   *  seul moment où il ne devait pas : sur un iPhone qui venait de lever la
   *  main, on lisait « Appareil validé » et un bouton « Continuer » pendant que
   *  la demande dormait encore, non tranchée, sur l'iPad (constaté le 16/08).
   *  De la sécurité de façade — l'accès réel restait bien refusé par le
   *  serveur, mais l'utilisateur n'avait aucun moyen de le savoir.
   *
   *  L'état est désormais `'asked'` : demande envoyée, rien de plus. Le passage
   *  à « Appareil validé » ne peut plus venir que du serveur, via `scan()`. */
  const ask = () => run(async () => {
    await askApproval();
    // Armer localement le retour évite une course : si l'autre appareil
    // répond avant le prochain GET, le demandeur doit quand même afficher le
    // bref accusé « Appareil validé ».
    wasWaiting.current = true;
  }, 'asked');

  const run = async (fn: () => Promise<void>, outcome: Done) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await fn();
      setDone(outcome);
      void scan();
    } catch (e) {
      // `NotAllowedError` = l'utilisateur a annulé ou le capteur a refusé. Ce
      // n'est pas une panne : le dire comme une panne inquiéterait pour rien.
      const msg = e instanceof Error && e.name === 'NotAllowedError'
        ? 'Vérification annulée.'
        : e instanceof Error ? e.message : 'Opération impossible.';
      setErr(msg);
    }
    setBusy(false);
  };

  const btn = 'emboss flex-1 rounded-2xl py-2.5 text-xs font-black disabled:opacity-40 active:scale-[.98] transition';
  /** Les confirmations d'appareil disparaissent seules. Les autres succès
   *  historiques gardent leur bouton explicite. */
  const good = done !== null && done !== 'asked' && done !== 'trusted';

  const nomsArrivées = arrivées.map(d => d.label ?? 'Appareil inconnu').join(', ');

  const title = annonce
    ? (arrivées.length > 1 ? `${arrivées.length} nouveaux appareils connectés` : 'Nouvel appareil connecté')
    : done === 'cleared' ? 'Compte sécurisé'
    : done === 'trusted' ? 'Appareil validé'
    : done === 'created' ? 'Clé d’accès créée'
    : (done === 'asked' && !expiré) ? 'Demande envoyée'
    : held ? 'Compte en vérification'
    : unknownDevice ? 'Nouvel appareil'
    : hasRequests ? (dort
        ? (toApprove.length > 1
            ? `${toApprove.length} appareils attendent votre décision`
            : 'Un appareil attend votre décision')
        : (toApprove.length > 1
            ? `${toApprove.length} appareils demandent l’accès`
            : 'Un appareil demande l’accès'))
    : 'Votre compte est ouvert sur un autre appareil';

  const body = annonce
    ? `${nomsArrivées} vient de se connecter à votre compte. Il ne pourra rien gérer tant que vous ne l’aurez pas autorisé.`
    : done === 'cleared' ? 'Les autres appareils ont été déconnectés et votre compte est réactivé.'
    : done === 'trusted' ? 'Cet appareil peut désormais gérer votre compte.'
    : done === 'created' ? 'Vous pourrez désormais reprendre la main sur ce compte depuis cet appareil.'
    // Le décompte est DANS LE BOUTON, et nulle part ailleurs : le répéter ici
    // faisait lire deux fois la même seconde qui s'écoule (19/08).
    : (done === 'asked' && !expiré) ? 'Autorisez cet appareil depuis un de vos appareils déjà connectés. Cet écran se mettra à jour tout seul dès que ce sera fait.'
    : expiré ? 'La demande a expiré sans réponse. Vous pouvez en envoyer une nouvelle.'
    : held ? 'Les modifications sont suspendues. Validez votre clé d’accès pour réactiver le compte et déconnecter les autres appareils.'
    : unknownDevice
      ? `Cet appareil doit être autorisé depuis un de vos appareils déjà connectés${
          dev.primaryLabel ? `, par exemple ${dev.primaryLabel}` : ''}.`
    : hasRequests
      // SANS CLÉ, ON NE PEUT PAS TRANCHER (constaté le 20/09). Décider passe
      // par une vérification biométrique ; un compte qui n'a aucune clé voyait
      // donc « Créer ma clé d'accès » sous un titre qui annonçait une demande
      // d'accès, sans un mot pour relier les deux. On le dit.
      ? `${toApprove.map(d => d.label ?? 'Appareil inconnu').join(', ')} — ${
          sec?.hasPasskey === false
            ? 'pour répondre, créez d’abord votre clé d’accès sur cet appareil : c’est elle qui prouve que la décision vient bien de vous.'
            : dort ? 'en attente depuis avant cette connexion. N’autorisez que si c’est bien vous.'
                   : 'autorisez-le seulement si c’est vous.'}`
    : null;

  // TANT QUE CE N'EST PAS TRANCHÉ, PLUS RIEN NE PASSE.
  //
  // Le gel serveur suffit à empêcher qu'une action aboutisse, mais il la laisse
  // partir : l'écran continue de répondre, la commande semble passer, et le
  // refus n'arrive qu'après. Pire, entre la détection et la réponse de la
  // personne, rien n'est encore gelé côté serveur — quelqu'un peut commander
  // une réparation et faire débiter la carte pendant que l'alerte est affichée
  // (signalé le 11/08).
  //
  // Le voile ferme donc l'interface dès la DÉTECTION, avant même la
  // contestation. Il ne remplace pas le gel serveur — un voile se retire avec
  // n'importe quel outil de développement — il évite juste qu'on agisse sans y
  // penser pendant qu'une question de sécurité est posée à l'écran.
  //
  // « C'est moi » le retire immédiatement. Il ne s'applique pas au message de
  // confirmation qui suit (`good`), qui n'attend plus rien de personne.
  //
  // UNE DEMANDE DORMANTE NE BLOQUE PAS. Personne n'attend derrière son écran :
  // barrer l'application entière pour une demande d'hier serait une rançon,
  // pas une garde. Elle s'annonce, elle se tranche, elle s'écarte.
  // `annonce` n'apparaît que lorsque les trois conditions ci-dessous sont
  // fausses : une arrivée ne peut donc jamais voiler l'écran, par construction.
  const bloquant = !good && !dort && (held || unknownDevice || hasRequests);

  return (
    <div className="fixed inset-x-0 top-0 z-[3000] flex justify-center px-3 pointer-events-none"
      style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>

      {bloquant && (
        <div aria-hidden
          className={`fixed inset-0 -z-10 pointer-events-auto bg-black/25 backdrop-blur-[2px]
            transition-opacity duration-[260ms] ${closing ? 'opacity-0' : 'opacity-100'}`}
          // Le voile avale tout : clic, molette, tactile. Sans ça, on peut
          // encore faire défiler la page dessous et croire qu'elle est vivante.
          onClickCapture={e => { e.preventDefault(); e.stopPropagation(); }}
          onTouchMove={e => e.preventDefault()}
          onWheel={e => e.preventDefault()} />
      )}

      <div role={annonce ? 'status' : 'alertdialog'} aria-labelledby="session-alert-title"
        className={`drop-in pointer-events-auto w-full max-w-[420px] rounded-3xl p-3.5
          bg-[var(--nav-bg)] backdrop-blur-lg shadow-[var(--sh-float)]
          transition-all duration-[260ms] ease-out ${
          closing ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'} ${done === 'trusted' ? 'cursor-pointer' : ''}`}
        onClick={done === 'trusted' ? dismiss : undefined}
        style={{ backgroundImage: 'var(--bump)' }}>

        <div className="flex items-start gap-3">
          <span className={`relative w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${
            good ? 'text-[var(--accent-ink)]' : held ? 'text-red-400'
              : annonce ? 'text-[var(--ink-2)]' : 'text-amber-500'}`}>
            <span aria-hidden style={{ filter: 'blur(2.5px)' }}
              className="absolute inset-0 rounded-xl shadow-[var(--sh-emboss)]" />
            <span className="relative">
              {good
                ? <svg width="17" height="17" viewBox="0 0 24 24" {...stroke}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
                : held
                  ? <svg width="17" height="17" viewBox="0 0 24 24" {...stroke}><rect x="4" y="10" width="16" height="11" rx="2.5" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
                  : <svg width="17" height="17" viewBox="0 0 24 24" {...stroke}><rect x="5" y="2" width="14" height="20" rx="2.5" /><path d="M11 18h2" /></svg>}
            </span>
          </span>

          <div className="min-w-0 flex-1">
            <div id="session-alert-title" className="text-sm font-black text-[var(--ink-1)]">{title}</div>
             {body && <p className="text-xs text-[var(--ink-2)] leading-snug mt-1">{body}</p>}
            {err && <p className="text-xs font-bold text-red-400 mt-1.5">{err}</p>}
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          {annonce ? (
            // Pas de décision à prendre ici : la carte s'en va toute seule. Le
            // bouton n'est qu'un raccourci vers la liste des appareils, pour
            // qui veut vérifier tout de suite.
            <a href="/compte?vue=securite" className={`${btn} text-[var(--ink-2)] text-center`}>
              <span className="relative">Voir mes appareils</span>
            </a>
          ) : good ? (
            <button type="button" onClick={dismiss} className={`${btn} text-[var(--accent-ink)]`}>
              <span className="relative">Continuer</span>
            </button>
          ) : done === 'trusted' ? (
            // Confirmation courte : l'effet la retire au bout de trois
            // secondes ; cliquer n'est qu'un raccourci, pas une étape.
            <button type="button" onClick={e => { e.stopPropagation(); dismiss(); }} className={`${btn} text-[var(--accent-ink)]`}>
              <span className="relative">OK</span>
            </button>
          ) : sec?.hasPasskey === false && !unknownDevice ? (
            // Un compte sans autre appareil peut créer sa première clé ici.
            // Si un autre appareil existe, la priorité est la demande
            // d'autorisation : créer une clé sur le nouvel appareil AVANT
            // l'accord distant inversait le flux de sécurité.
            <>
              <button type="button" onClick={dismiss} disabled={busy} className={`${btn} text-[var(--ink-2)]`}>
                <span className="relative">Plus tard</span>
              </button>
              <button type="button" onClick={() => run(createPasskey, 'created')} disabled={busy}
                className={`${btn} text-[var(--accent-ink)]`}>
                <span className="relative">{busy ? 'Création…' : 'Créer ma clé d’accès'}</span>
              </button>
            </>
          ) : held ? (
            <button type="button" onClick={() => run(verifyWithPasskey, 'cleared')} disabled={busy}
              className={`${btn} text-[var(--accent-ink)]`}>
              <span className="relative">{busy ? 'Vérification…' : 'Vérifier et reprendre la main'}</span>
            </button>
          ) : unknownDevice ? (
            // Un nouvel appareil ne s'auto-valide jamais. Le bouton ne fait
            // qu'émettre la demande ; la décision et la passkey appartiennent
            // à un appareil déjà de confiance.
            <button type="button" onClick={ask} disabled={busy || attend}
              className={`${btn} text-[var(--accent-ink)]`}>
              <span className="relative inline-flex items-center justify-center gap-2">
                {attend && (
                  <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
                )}
                {busy ? 'Envoi…' : attend ? 'En attente d’autorisation' : 'Demander l’autorisation'}
                {/* Le décompte À CÔTÉ du témoin, pas à sa place : le rond qui
                    tourne dit « ça vit », le chiffre dit « jusqu'à quand ».
                    `tabular-nums` fige la largeur — sans ça le bouton tressaute
                    à chaque seconde, sur des chiffres de largeurs différentes. */}
                {attend && chrono && (
                  <span className="tabular-nums font-mono opacity-70">{chrono}</span>
                )}
              </span>
            </button>
          ) : hasRequests ? (
            <>
              {dort && (
                // Écarter, pas décider. La carte revient au prochain lancement
                // — et la pastille rouge de Sécurité, elle, ne s'éteint pas.
                <button type="button" onClick={() => setÉcartés(v => [...v, ...toApprove.map(d => d.id)])}
                  disabled={busy} className={`${btn} text-[var(--ink-2)]`}>
                  <span className="relative">Plus tard</span>
                </button>
              )}
              <button type="button" onClick={() => decide(toApprove[0].id, false)} disabled={busy}
                className={`${btn} text-red-400`}>
                <span className="relative">{dort ? 'Refuser' : 'Ce n’est pas moi'}</span>
              </button>
              <button type="button" onClick={() => decide(toApprove[0].id, true)} disabled={busy}
                className={`${btn} text-[var(--accent-ink)]`}>
                <span className="relative">{busy ? 'Vérification…' : dort ? 'Autoriser' : 'C’est moi'}</span>
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SessionAlert;
